/**
 * Request/response contracts, defined once. The API validates with these and
 * the web client imports the inferred types, so the two cannot drift apart.
 */
import { z } from 'zod';
import { CHALLENGE_BANDS, MAX_CHALLENGE_MESSAGE_LENGTH } from './challenges.js';
import { FORMATS, MAX_DISPLAY_NAME_LENGTH, MAX_NOTES_LENGTH, MAX_PASSWORD_LENGTH, MAX_SCORE, MAX_WIN_SCORE, MIN_PASSWORD_LENGTH, MIN_WIN_SCORE, MATCH_STATUSES, PLAYERS_PER_SIDE, POSITIONS, SIDES, USER_ROLES, USER_STATUSES, } from './constants.js';
// --- Primitives --------------------------------------------------------
export const idSchema = z.coerce.number().int().positive();
export const formatSchema = z.enum(FORMATS);
export const sideSchema = z.enum(SIDES);
export const positionSchema = z.enum(POSITIONS);
export const matchStatusSchema = z.enum(MATCH_STATUSES);
const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
    .max(MAX_PASSWORD_LENGTH);
const displayNameSchema = z.string().trim().min(2).max(MAX_DISPLAY_NAME_LENGTH);
const emailSchema = z.email().max(255).toLowerCase();
// --- Auth --------------------------------------------------------------
export const registerSchema = z.object({
    email: emailSchema,
    displayName: displayNameSchema,
    password: passwordSchema,
    /** Required only when the server has INVITE_CODE configured. */
    inviteCode: z.string().max(200).optional(),
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(MAX_PASSWORD_LENGTH),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(MAX_PASSWORD_LENGTH),
    newPassword: passwordSchema,
});
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
export const updateProfileSchema = z
    .object({
    displayName: displayNameSchema.optional(),
    avatarUrl: z.url().max(500).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
// --- Matches -----------------------------------------------------------
export const matchPlayerInputSchema = z.object({
    userId: idSchema,
    side: sideSchema,
    position: positionSchema.optional(),
});
/**
 * Shape-level validation only. Rules needing database context — that the
 * season is active, that the scoreline reaches that season's `win_score`,
 * that the submitter is a participant — live in `matchService`.
 */
export const submitMatchSchema = z
    .object({
    seasonId: idSchema.optional(),
    format: formatSchema,
    playedAt: z.iso.datetime({ offset: true }).optional(),
    scoreA: z.number().int().min(0).max(MAX_SCORE),
    scoreB: z.number().int().min(0).max(MAX_SCORE),
    players: z.array(matchPlayerInputSchema).min(2).max(4),
    notes: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
    /**
     * Settles an accepted challenge. Optional — results are still submitted
     * freely, and a challenge is only ever one way a game gets arranged.
     */
    challengeId: idSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.scoreA === value.scoreB) {
        ctx.addIssue({
            code: 'custom',
            path: ['scoreB'],
            message: 'Foosball has no draws — the scores must differ',
        });
    }
    const perSide = PLAYERS_PER_SIDE[value.format];
    const sideA = value.players.filter((p) => p.side === 'A');
    const sideB = value.players.filter((p) => p.side === 'B');
    if (sideA.length !== perSide || sideB.length !== perSide) {
        ctx.addIssue({
            code: 'custom',
            path: ['players'],
            message: `A ${value.format} match needs exactly ${perSide} player(s) per side`,
        });
    }
    const ids = value.players.map((p) => p.userId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: 'custom',
            path: ['players'],
            message: 'A player cannot appear twice in the same match',
        });
    }
});
export const disputeMatchSchema = z.object({
    reason: z.string().trim().min(1).max(MAX_NOTES_LENGTH),
});
export const listMatchesQuerySchema = z.object({
    seasonId: idSchema.optional(),
    playerId: idSchema.optional(),
    status: matchStatusSchema.optional(),
    format: formatSchema.optional(),
    /** Keyset pagination cursor — the id of the last row on the previous page. */
    cursor: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
// --- Seasons -----------------------------------------------------------
export const createSeasonSchema = z.object({
    name: z.string().trim().min(2).max(80),
    startsOn: z.iso.date(),
    endsOn: z.iso.date().nullable().optional(),
    winScore: z.number().int().min(MIN_WIN_SCORE).max(MAX_WIN_SCORE).optional(),
    pointsWin: z.number().int().min(0).max(100).optional(),
    pointsLoss: z.number().int().min(0).max(100).optional(),
    isActive: z.boolean().optional(),
});
export const updateSeasonSchema = createSeasonSchema.partial().refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export const standingsQuerySchema = z.object({
    format: z.union([formatSchema, z.literal('all')]).default('all'),
});
// --- Response shapes ---------------------------------------------------
/** A user as exposed by the API. Never carries `password_hash`. */
export const publicUserSchema = z.object({
    id: z.number().int().positive(),
    email: z.email(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    role: z.enum(USER_ROLES),
    status: z.enum(USER_STATUSES),
    createdAt: z.string(),
});
export const authResponseSchema = z.object({
    accessToken: z.string(),
    user: publicUserSchema,
});
// --- Cross-cutting rules ----------------------------------------------
/**
 * Is this a legal final scoreline for a season with the given target?
 *
 * "Reach the target" is read inclusively: the winner must be at or above
 * `winScore`, which permits win-by-two overtime (12–10) as well as the
 * ordinary 10–7, while still rejecting an abandoned 4–2.
 */
export function isValidFinalScore(scoreA, scoreB, winScore) {
    if (!Number.isInteger(scoreA) || !Number.isInteger(scoreB))
        return false;
    if (scoreA < 0 || scoreB < 0)
        return false;
    if (scoreA === scoreB)
        return false;
    return Math.max(scoreA, scoreB) >= winScore;
}
/** Players required in total for a format (both sides). */
export function totalPlayersFor(format) {
    return PLAYERS_PER_SIDE[format] * 2;
}
// --- Challenges --------------------------------------------------------
export const challengeBandSchema = z.enum(CHALLENGE_BANDS);
export const challengeCandidatesQuerySchema = z.object({
    format: formatSchema.default('1v1'),
    /** Omit to receive every opponent, grouped into buckets. */
    band: challengeBandSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const createChallengeSchema = z.object({
    opponentId: idSchema,
    format: formatSchema.default('1v1'),
    message: z.string().trim().max(MAX_CHALLENGE_MESSAGE_LENGTH).optional(),
});
//# sourceMappingURL=schemas.js.map